import { FC, useEffect, useState } from 'react';
import { css } from '@emotion/react';
import DOMPurify from 'dompurify';
import { theme } from '@frontend/theme';

const QUILL_STYLESHEET_URL = 'https://cdn.quilljs.com/1.3.6/quill.snow.css';

// Regex to detect if the content is from Quill editor
const QUILL_CONTENT_REGEX = /class="[^"]*\bql-/;

// Open links in a new tab safely. Registered once at module load.
DOMPurify.addHook('afterSanitizeAttributes', (node) => {
  if (node.tagName === 'A' && node.hasAttribute('href')) {
    node.setAttribute('target', '_blank');
    node.setAttribute('rel', 'noopener noreferrer');
  }
});

function injectQuillStylesheet() {
  if (document.querySelector(`link[href="${QUILL_STYLESHEET_URL}"]`)) return;
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = QUILL_STYLESHEET_URL;
  document.head.appendChild(link);
}

const richTextStyle = css`
  p {
    color: ${theme.colors.text.default};
  }
  a {
    color: ${theme.colors.text.interactive};
    text-decoration: underline;
    cursor: pointer;
  }
  .editor-nested-listitem {
    list-style-type: none;
  }
`;

// quill.snow.css gives `.ql-editor` its own padding. `&.ql-editor` outranks it regardless of stylesheet load order.
const quillContainerStyle = css`
  &.ql-editor {
    padding: 0;
  }
`;

interface RichTextFieldProps {
  fieldKey: string;
  htmlContent: string;
}

export const RichTextField: FC<RichTextFieldProps> = ({ fieldKey, htmlContent }) => {
  const [sanitizedValue, setSanitizedValue] = useState('');
  const [hasQuillContent, setHasQuillContent] = useState(false);

  useEffect(() => {
    if (QUILL_CONTENT_REGEX.test(htmlContent)) {
      injectQuillStylesheet();
      setHasQuillContent(true);
    }

    const _sanitizedValue = DOMPurify.sanitize(htmlContent ?? '', {
      USE_PROFILES: { html: true },
      ADD_ATTR: ['target'],
    });
    setSanitizedValue(_sanitizedValue);
  }, [htmlContent]);

  return (
    /**
     * The `section` tag is used to wrap the content of the rich text field.
     * The `ql-editor` class will otherwise take the entire height of the page and mess up the layout.
     */
    <section id={`rich-text-field-${fieldKey}`}>
      <div className={hasQuillContent ? 'ql-editor' : undefined} css={quillContainerStyle}>
        <div
          css={richTextStyle}
          dangerouslySetInnerHTML={{
            __html: sanitizedValue,
          }}
        />
      </div>
    </section>
  );
};
